import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import {Session, User} from '@supabase/supabase-js';
import {supabase} from '../../api/supabase';

interface AuthState {
  user: User | null;
  session: Session | null;
  status: 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error';
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  session: null,
  status: 'idle',
  error: null,
};

interface SignUpCredentials {
  email: string;
  password: string;
}

interface SignUpResult {
  user: User | null;
  session: Session | null;
}

export const signUp = createAsyncThunk<SignUpResult, SignUpCredentials>(
  'auth/signUp',
  async ({email, password}, {rejectWithValue}) => {
    try {

      // Make sure we're not accidentally doing anonymous auth by checking inputs
      if (!email || !password) {
        throw new Error('Email and password are required for signup');
      }

      const {data, error} = await supabase.auth.signUp({
        email,
        password,
        options: {
          // Explicitly request email/password auth
          emailRedirectTo: window.location.origin,
        },
      });

      if (error) {

        // Handle specific duplicate user errors like mobile version
        if (error.message?.includes('already registered') ||
            error.message?.includes('already exists') ||
            error.message?.includes('User already registered') ||
            error.code === 'user_already_exists' ||
            error.message?.toLowerCase().includes('duplicate')) {
          throw new Error('This email is already registered. Please try logging in instead.');
        }
        throw error;
      }

      if (!data || !data.user) {
        throw new Error('Sign up process failed to return user data.');
      }

      return {
        user: data.user,
        session: data.session,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'An unknown sign-up error occurred.',
      );
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthState: (
      state,
      action: PayloadAction<{user: User | null; session: Session | null}>,
    ) => {
      state.user = action.payload.user;
      state.session = action.payload.session;
      state.status = action.payload.user ? 'authenticated' : 'unauthenticated';
      state.error = null;
    },
    clearAuthState: state => {
      state.user = null;
      state.session = null;
      state.status = 'unauthenticated';
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(signUp.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(
        signUp.fulfilled,
        (state, action: PayloadAction<SignUpResult>) => {
          state.user = action.payload.user;
          state.session = action.payload.session;
          if (action.payload.user && action.payload.session) {
            state.status = 'authenticated';
          } else if (!action.payload.user && action.payload.session) {
            state.status = 'unauthenticated';
          } else {
            state.status = 'unauthenticated';
          }
          state.error = null;
        },
      )
      .addCase(signUp.rejected, (state, action) => {
        state.status = 'error';
        state.error =
          (action.payload as string) ||
          action.error.message ||
          'Sign up failed';
        state.user = null;
        state.session = null;
      });
  },
});

export const {setAuthState, clearAuthState} = authSlice.actions;
export default authSlice.reducer;
